import { useInfiniteQuery } from "@tanstack/react-query";
import { playlistService } from "../api/services/playlistService";
import type { Playlist, PlaylistFilters, PlaylistResult } from "../schemas/playlistSchema";
import { DEFAULT_PAGE_SIZE } from "../constants";

export type PlaylistsInfiniteFilters = Omit<
  PlaylistFilters,
  "page" | "pageSize"
> & {
  pageSize?: number;
};

export const usePlaylistsInfinite = (filters: PlaylistsInfiniteFilters = {}) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;

  const query = useInfiniteQuery<PlaylistResult, Error>({
    queryKey: ["playlists", "infinite", { ...rest, pageSize }],
    queryFn: ({ pageParam }) =>
      playlistService.getAll({
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
  });

  const playlists: Playlist[] =
    query.data?.pages.flatMap((p) => p.playlists) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    playlists,
    total,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
